/**
 * Customer feedback form: star rating, message, optional attachments, emailed to support.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  ChatBubbleLeftRightIcon,
  PaperClipIcon,
  StarIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth, supabase } from '../context/AuthContext';
import CustomerLayout from './CustomerLayout';

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const inputClassName =
  'w-full rounded-[1rem] border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100';

const FeedbackForm = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    feedback: '',
    rating: 0,
    name: '',
    email: '',
    phone: '',
    files: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [fileError, setFileError] = useState('');
  const [hoveredStar, setHoveredStar] = useState(0);

  // Prefills name/email/phone from the users table when signed in.
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!user?.id) {
        setFormData((prev) => ({
          ...prev,
          name: '',
          email: user?.email || '',
          phone: user?.phone || '',
        }));
        return;
      }

      try {
        const { data, error } = await supabase
          .from('users')
          .select('name, email, phone')
          .eq('id', user.id)
          .limit(1)
          .maybeSingle();

        if (error) {
          throw error;
        }

        // Name is often null until the customer places their first order.
        setFormData((prev) => ({
          ...prev,
          name: data?.name?.trim() || '',
          email: data?.email || user?.email || '',
          phone: data?.phone || user?.phone || '',
        }));
      } catch (error) {
        console.error('Error fetching user details:', error);
        setFormData((prev) => ({
          ...prev,
          name: '',
          email: user?.email || '',
          phone: user?.phone || '',
        }));
      }
    };

    fetchUserDetails();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Only images, PDFs and Word docs up to 25MB are accepted.
  const validateFile = (file) => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return 'File type not supported. Please upload images, PDFs, or Word documents.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size too large. Maximum size is 25MB.';
    }
    return null;
  };

  // Unique enough to skip duplicates when the same file is picked twice.
  const getFileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;

  // Adds selected files after type/size checks; rejects the whole pick if any file fails.
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFileError('');

    if (selectedFiles.length === 0) {
      return;
    }

    for (const file of selectedFiles) {
      const error = validateFile(file);
      if (error) {
        setFileError(error);
        e.target.value = '';
        return;
      }
    }

    setFormData((prev) => {
      const existingKeys = new Set(prev.files.map(getFileKey));
      const filesToAdd = selectedFiles.filter((file) => !existingKeys.has(getFileKey(file)));
      return {
        ...prev,
        files: [...prev.files, ...filesToAdd],
      };
    });

    // Clear the input so the same file can be chosen again after removal.
    e.target.value = '';
  };

  const handleRemoveFile = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, index) => index !== indexToRemove),
    }));
    setFileError('');

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Sends feedback (and any attachments) through the send-feedback edge function.
  // Email is required; name falls back to "New User (unknown)" when blank.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setFileError('');

    const contactName = formData.name || 'New User (unknown)';
    const contactEmail = formData.email || user?.email || '';
    const contactPhone = formData.phone ? `+${formData.phone.replace(/^\+/, '')}` : 'Not provided';
    const subjectName = formData.name || contactEmail || 'Customer';

    if (!contactEmail) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const filePromises = formData.files.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64String = reader.result.split(',')[1];
              resolve({
                filename: file.name,
                content: base64String,
                type: file.type,
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
      );

      const attachments = await Promise.all(filePromises);

      const { error } = await supabase.functions.invoke('send-email', {
        body: {
          email: 'modelreports@mdlreports.com.au',
          subject: `Feedback from ${subjectName}`,
          message: `
            <h3>New Feedback/Query Submission</h3>
            <p><strong>Name:</strong> ${contactName}</p>
            <p><strong>Email:</strong> ${contactEmail}</p>
            <p><strong>Phone:</strong> ${contactPhone}</p>
            <p><strong>Rating:</strong> ${formData.rating}/5</p>
            <p><strong>Feedback:</strong></p>
            <p>${formData.feedback}</p>
          `,
          attachments,
        },
      });

      if (error) {
        throw error;
      }

      setSubmitStatus('success');
      setFormData((prev) => ({
        ...prev,
        feedback: '',
        rating: 0,
        files: [],
      }));

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      setTimeout(() => {
        setSubmitStatus(null);
      }, 7000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const activeStar = hoveredStar || formData.rating;

  return (
    <CustomerLayout
      activeItemId="feedback"
      title="Feedback / Query"
      subtitle="Share suggestions, questions, or issues and we'll get back to you."
      contentClassName="mx-auto w-full max-w-none"
    >
      <div className="grid items-start gap-6 pb-10 xl:grid-cols-[minmax(0,1.7fr)_minmax(320px,0.9fr)] xl:pb-16">
        <section className="order-2 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm shadow-slate-200/60 xl:order-1">
          <div className="px-5 pt-5 pb-3 sm:px-6">
            <h2 className="flex items-center gap-2 text-md font-medium text-slate-900 uppercase tracking-[0.04em]">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-600" />
              Submit Feedback
            </h2>
          </div>

          <div className="border-t border-slate-200 px-5 py-5 sm:px-6 sm:py-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Do you have any suggestions to improve our product &amp; service, or a query?
                </label>
                <textarea
                  name="feedback"
                  value={formData.feedback}
                  onChange={handleInputChange}
                  className={inputClassName}
                  rows={4}
                  placeholder="Share your feedback or question here..."
                  required
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Add supporting photos/files{' '}
                  <span className="font-normal text-slate-400">(optional)</span>
                </label>
                <label className="flex cursor-pointer flex-col items-center justify-center rounded-[1rem] border-2 border-dashed border-slate-200 bg-slate-50/80 px-4 py-6 text-center transition hover:border-blue-300 hover:bg-blue-50/40">
                  <PaperClipIcon className="h-6 w-6 text-slate-400" />
                  <span className="mt-2 text-sm font-medium text-slate-700">
                    {formData.files.length > 0 ? 'Add more files' : 'Click to upload files'}
                  </span>
                  <span className="mt-1 text-xs text-slate-500">
                    Images, PDF, or Word documents up to 25MB each
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    multiple
                    accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx"
                    className="sr-only"
                  />
                </label>
                {fileError && (
                  <p className="mt-2 rounded-[0.75rem] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {fileError}
                  </p>
                )}
                {formData.files.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {formData.files.map((file, index) => (
                      <div
                        key={`${getFileKey(file)}-${index}`}
                        className="flex items-center gap-2 rounded-[0.75rem] border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
                      >
                        <PaperClipIcon className="h-4 w-4 shrink-0 text-blue-600" />
                        <span className="min-w-0 flex-1 truncate">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(index)}
                          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                          aria-label={`Remove ${file.name}`}
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-[1rem] border border-slate-200 bg-slate-50/80 px-4 py-4">
                <label className="mb-3 block text-center text-sm font-medium text-slate-700">
                  How satisfied are you with our company/service overall?
                </label>
                <div
                  className="flex justify-center gap-1"
                  onMouseLeave={() => setHoveredStar(0)}
                >
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, rating: star }))}
                      onMouseEnter={() => setHoveredStar(star)}
                      className="rounded-lg p-1 transition hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      aria-label={`Rate ${star} out of 5`}
                    >
                      {star <= activeStar ? (
                        <StarIcon className="h-7 w-7 text-amber-400" />
                      ) : (
                        <StarOutlineIcon className="h-7 w-7 text-slate-300" />
                      )}
                    </button>
                  ))}
                </div>
                {formData.rating > 0 && (
                  <p className="mt-2 text-center text-xs text-slate-500">
                    You rated us {formData.rating} out of 5
                  </p>
                )}
              </div>

              {submitStatus === 'success' && (
                <div className="rounded-[1rem] border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-600 shadow-sm">
                  Thank you for your feedback! We&apos;ll get back to you soon.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="rounded-[1rem] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
                  There was an error submitting your feedback. Please try again.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center rounded-[0.9rem] bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </form>
          </div>
        </section>

        <aside className="order-1 self-start overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm shadow-slate-200/60 xl:order-2">
          <div className="px-5 py-5 sm:px-6 sm:py-6">
            <h2 className="mb-4 flex items-center gap-2 text-md font-medium text-slate-900 uppercase tracking-[0.04em]">
              <InformationCircleIcon className="h-5 w-5 text-blue-600" />
              How it works
            </h2>
            <div className="space-y-3 text-sm text-slate-600">
              <div className="rounded-[1rem] border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                1. Describe your feedback, suggestion, or question in the message field.
              </div>
              <div className="rounded-[1rem] border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                2. Optionally attach supporting files such as screenshots or documents.
              </div>
              <div className="rounded-[1rem] border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                3. Rate your overall satisfaction with our service.
              </div>
              <div className="rounded-[1rem] border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                4. Submit the form and our team will respond via your account email.
              </div>
            </div>

            <div className="mt-6 border-t border-slate-200 pt-5">
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.04em] text-slate-900">
                Need Help?
              </h3>
              <div className="rounded-[1rem] border border-blue-200 bg-blue-50/80 px-4 py-3 text-sm text-blue-700 shadow-sm">
                Need immediate assistance? Call our support team at +61 432 333 404.
              </div>
              <div className="mt-3 rounded-[1rem] border border-blue-200 bg-blue-50/80 px-4 py-3 text-sm text-blue-700 shadow-sm">
                Support Hours
                <ul className="mt-1 list-inside list-disc">
                  <li>Mon-Fri: 9:00 AM - 5:00 PM</li>
                  <li>Sat-Sun: Closed</li>
                </ul>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </CustomerLayout>
  );
};

export default FeedbackForm;
